// Observability is intentionally lazy: when VITE_SENTRY_DSN isn't set we
// don't even pull the @sentry/react bundle into the page. This keeps the
// vendor-sentry chunk out of the initial download path and means Sentry
// module init can never crash the app shell.

type SentryNs = typeof import("@sentry/react");
let sentry: SentryNs | null = null;
let initialised = false;
let pendingUser: { id: string; email: string } | null = null;
let pendingClear = false;

export async function initObservability(): Promise<void> {
  if (initialised) return;
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? undefined;
  if (!dsn) return;
  try {
    sentry = await import("@sentry/react");
    sentry.init({
      dsn,
      environment: (import.meta.env.MODE as string) || "production",
      release: (import.meta.env.VITE_RELEASE as string | undefined) || undefined,
      integrations: [sentry.browserTracingIntegration()],
      tracesSampleRate: 0.2,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.request?.headers) {
          delete (event.request.headers as Record<string, string>).Cookie;
          delete (event.request.headers as Record<string, string>).cookie;
        }
        return event;
      },
    });
    initialised = true;
    if (pendingUser) sentry.setUser(pendingUser);
    if (pendingClear) sentry.setUser(null);
  } catch {
    // eslint-disable-next-line no-console
    console.warn("[observability] sentry init skipped");
  }
}

export function captureRenderError(error: Error, info?: { componentStack?: string }): void {
  if (!initialised || !sentry) return;
  try {
    sentry.withScope(scope => {
      scope.setExtras({ componentStack: info?.componentStack });
      sentry!.captureException(error);
    });
  } catch {
    // swallow - never let observability break the UI
  }
}

export function setAuthUser(user: { email?: string; id?: string } | null): void {
  if (!initialised || !sentry) {
    if (!user || !user.email) {
      pendingClear = true;
      pendingUser = null;
    } else {
      pendingUser = { id: user.id ?? user.email, email: user.email };
    }
    return;
  }
  try {
    if (!user || !user.email) {
      sentry.setUser(null);
      return;
    }
    sentry.setUser({ id: user.id ?? user.email, email: user.email });
  } catch {
    // ignore
  }
}
